import axios from "axios";
import store from "../redux/store/store";

const API_URL = import.meta.env.VITE_API_URL;

const UserTaskService = {
  // Fetch all user tasks with pagination and filtering
  fetchUserTasks: async (userId, filter = {}, page = 1) => {
    const userToken = store.getState()?.auth?.user?.accessToken?.token;
    try {
      const response = await axios.get(`${API_URL}/UserTask`, {
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
        params: {
          PageNumber: Number(page),
          PageSize: filter.pageSize || 6,
          SearchText: filter.search || "",
          UserId: userId,
          ia: true,
          parentId: 0, // Ana görevleri getir
          sub_task: 0, // Alt görev olmayanları getir
          sortBy: filter.sortBy || "createDate",
          sortDirection: filter.sortDirection || "desc",
        },
      });

      let items = [];
      let totalCount = 0;

      // Handle the specific nested structure from the API
      if (response.data?.status === true && response.data?.data) {
        const responseData = response.data.data;
        totalCount = responseData.recordsTotal || 0;
        items = responseData.data || [];
      }

      return {
        items,
        totalCount,
        currentPage: page,
      };
    } catch (error) {
      console.error("Error fetching user tasks:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
      });
      return {
        items: [],
        totalCount: 0,
        currentPage: page,
      };
    }
  },

  // Get a single user task by ID
  getUserTaskById: async (taskId) => {
    const userToken = store.getState()?.auth?.user?.accessToken?.token;
    try {
      const response = await axios.get(`${API_URL}/UserTask`, {
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
        params: {
          id: taskId,
          PageNumber: 1,
          PageSize: 1,
        },
      });

      if (!response.data?.status || !response.data?.data?.data) {
        console.error("Task data not found in response:", response.data);
        throw new Error("Task data not found");
      }

      // Get the first item from the data array
      const taskData = response.data.data.data[0];
      if (!taskData) {
        console.error("Task data array is empty");
        throw new Error("Task data not found");
      }

      const processedTask = {
        id: taskData.id || taskId,
        userId: taskData.userId,
        title: taskData.title || "Başlıksız Talep",
        detail: taskData.detail || "",
        content: taskData.detail || "",
        state: taskData.state || 0,
        createDate: taskData.createDate || new Date().toISOString(),
        updateDate:
          taskData.updateDate ||
          taskData.createDate ||
          new Date().toISOString(),
        ia: taskData.ia ?? true,
        userName: taskData.userName || "Kullanıcı",
        user: taskData.user || {
          id: taskData.userId,
          name: taskData.userName || "Kullanıcı",
        },
        replyCount: taskData.replyCount || 0,
        sub_task: taskData.sub_task || 0,
      };

      return processedTask;
    } catch (error) {
      console.error("Error fetching user task details:", error);
      console.error("Error details:", error.response?.data || error.message);

      // Return a default task object with the ID in case of error
      const defaultTask = {
        id: taskId,
        userId: store.getState()?.auth?.user?.id,
        title: "Başlıksız Talep",
        detail: "",
        content: "",
        state: 0,
        createDate: new Date().toISOString(),
        updateDate: new Date().toISOString(),
        ia: true,
        userName: "Kullanıcı",
        user: { id: store.getState()?.auth?.user?.id, name: "Kullanıcı" },
        replyCount: 0,
        sub_task: 0, // Default to 0 for main tasks
      };

      return defaultTask;
    }
  },

  // Create a new user task
  createUserTask: async (taskData) => {
    const userToken = store.getState()?.auth?.user?.accessToken?.token;
    try {
      const formattedData = {
        title: taskData.title,
        detail: taskData.content,
        userId: taskData.userId,
        ia: true,
        parentId: taskData.parentId || null,
        sub_task: taskData.sub_task || 0,
        state: taskData.state || 1,
        userType: 'user',
      };

      const response = await axios.post(`${API_URL}/UserTask`, formattedData, {
        headers: {
          Authorization: `Bearer ${userToken}`,
          "Content-Type": "application/json",
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error creating user task:", error);
      throw error;
    }
  },

  // Add a sub task (reply)
  addSubTask: async (taskId, subTaskData) => {
    const userToken = store.getState()?.auth?.user?.accessToken?.token;
    try {
      console.log("Adding subtask with data:", { taskId, subTaskData });

      // Format data according to API requirements
      const formattedData = {
        parentId: Number(taskId),
        title: "",
        detail: subTaskData.detail,
        userId: subTaskData.userId,
        ia: true,
        subTask: Number(taskId),
      };

      console.log("Formatted request data:", formattedData);

      const response = await axios.post(`${API_URL}/UserTask`, formattedData, {
        headers: {
          Authorization: `Bearer ${userToken}`,
          "Content-Type": "application/json",
        },
      });

      console.log("Add subtask response:", response.data);

      if (!response.data?.status) {
        throw new Error("Failed to add subtask: " + response.data?.message);
      }

      return response.data;
    } catch (error) {
      console.error("Error adding sub task:", error);
      console.error("Error details:", error.response?.data || error.message);
      throw error;
    }
  },

  // Get sub tasks for a task
  getSubTasks: async (taskId) => {
    const userToken = store.getState()?.auth?.user?.accessToken?.token;
    try {
      console.log("Fetching subtasks for taskId:", taskId);
      const response = await axios.get(`${API_URL}/UserTask`, {
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
        params: {
          parentId: taskId,
          subTask: taskId,
          PageNumber: 1,
          PageSize: 100,
          ia: true,
        },
      });

      console.log("Raw subtasks response:", response.data);

      let subTasks = [];
      if (
        response.data?.status === true &&
        response.data?.data?.data &&
        Array.isArray(response.data.data.data)
      ) {
        subTasks = response.data.data.data
          .filter((task) => {
            console.log("Filtering task:", task);
            return task.ia !== false && task.subTask === taskId;
          })
          .map((task) => ({
            id: task.id,
            userId: task.userId,
            title: task.title || "",
            detail: task.detail || "",
            state: task.state,
            createDate: task.createDate || new Date().toISOString(),
            updateDate: task.updateDate,
            ia: task.ia || true,
            userName: task.userName || task.user?.name || "Kullanıcı",
            parentId: taskId,
            user: task.user || {},
            sub_task: taskId,
          }));

        console.log("Filtered and mapped subtasks:", subTasks);
      } else {
        console.log("No subtasks found or invalid response format");
      }

      return subTasks;
    } catch (error) {
      console.error("Error fetching sub tasks:", error);
      console.error("Error details:", error.response?.data || error.message);
      return [];
    }
  },

  deleteUserTask: async (taskId) => {
    const userToken = store.getState()?.auth?.user?.accessToken?.token;
    try {
      console.log(`Deleting task with ID: ${taskId}`);
      const response = await axios.delete(`${API_URL}/UserTask?id=${taskId}`, {
        headers: {
          Authorization: `Bearer ${userToken}`,
          "Content-Type": "application/json",
        },
      });

      console.log("Delete task response:", response.data);
      return {
        ...response.data,
        status: response.data?.status || response.status === 200,
      };
    } catch (error) {
      console.error("Error deleting user task:", error);
      console.error("Error details:", error.response?.data || error.message);
      throw error;
    }
  },
};

export default UserTaskService;
